import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Loader2, CheckCircle2, AlertCircle, Save, Zap } from 'lucide-react';

const AdminPaymentSettings = () => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [msg, setMsg] = useState({ kind: '', text: '' });

  const headers = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  });

  const loadStatus = async () => {
    try {
      const r = await fetch(`${API}/api/admin/payment-config/status`, { headers: headers() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      setStatus(await r.json());
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const save = async () => {
    setSaving(true);
    setMsg({ kind: '', text: '' });
    try {
      const r = await fetch(`${API}/api/admin/payment-config/stripe`, {
        method: 'PUT', headers: headers(),
        body: JSON.stringify({ stripe_api_key: stripeKey, stripe_webhook_secret: webhookSecret }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setMsg({ kind: 'ok', text: `Saved. Active key is now ${data.masked}` });
      setStripeKey('');
      setWebhookSecret('');
      await loadStatus();
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setMsg({ kind: '', text: '' });
    try {
      const r = await fetch(`${API}/api/admin/payment-config/test`, { method: 'POST', headers: headers() });
      const data = await r.json();
      if (data.success) {
        const parts = [
          `Mode: ${data.mode.toUpperCase()}`,
          data.business_name && `Account: ${data.business_name}`,
          data.country && `Country: ${data.country}`,
          `Charges: ${data.charges_enabled ? '✓ enabled' : '✗ disabled'}`,
          `Payouts: ${data.payouts_enabled ? '✓ enabled' : '✗ disabled'}`,
        ].filter(Boolean);
        setMsg({ kind: 'ok', text: parts.join(' · ') });
      } else {
        setMsg({ kind: 'err', text: data.error || 'Test failed' });
      }
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-3xl mx-auto">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-4" data-testid="back-to-dash">
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <CreditCard className="w-7 h-7 text-yellow-400" />
          <h1 className="text-3xl font-black">Payment settings</h1>
        </div>
        <p className="text-sm text-zinc-400 mb-6 max-w-2xl">
          Paste your Stripe secret key below and click Save. Takes effect in under a minute — no redeploy required. The key is stored in your database, encrypted at rest, and never shown in full after saving.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading current status…</div>
        ) : (
          <>
            {/* Current status card */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6" data-testid="stripe-status">
              <div className="font-bold mb-3">Current status</div>
              <div className="grid gap-2 text-sm">
                <StatusRow label="Active key" value={status?.active_key_masked || '— none —'} />
                <StatusRow label="Source" value={status?.active_key_source || 'none'} />
                <StatusRow
                  label="Mode"
                  value={status?.mode || 'unknown'}
                  pill={status?.mode === 'live' ? 'bg-green-500/20 text-green-300' : status?.mode === 'test' ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'}
                />
                <StatusRow label="Webhook secret" value={status?.webhook_secret_set ? '✓ set' : '— not set —'} />
                <StatusRow label="Env var key" value={status?.env_key_masked || '— none —'} />
                <StatusRow label="Database override" value={status?.db_key_masked || '— none —'} />
              </div>
              <button
                onClick={test}
                disabled={testing || !status?.active_key_masked}
                data-testid="stripe-test-btn"
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-sm font-bold disabled:bg-zinc-700"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Test the active key with Stripe
              </button>
            </div>

            {/* Edit form */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <div className="font-bold mb-3">Set a new key</div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                Stripe secret key
              </label>
              <input
                type="password"
                value={stripeKey}
                onChange={(e) => setStripeKey(e.target.value)}
                placeholder="sk_live_..."
                autoComplete="off"
                data-testid="stripe-key-input"
                className="w-full px-4 py-3 bg-black border border-zinc-700 rounded-lg text-white placeholder-zinc-600 focus:border-yellow-500 outline-none font-mono text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1 mb-4">
                Starts with <code>sk_live_</code> for real payments, or <code>sk_test_</code> for test mode. Find it at dashboard.stripe.com → Developers → API keys.
              </p>

              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                Webhook signing secret (optional)
              </label>
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => setWebhookSecret(e.target.value)}
                placeholder="whsec_..."
                autoComplete="off"
                data-testid="stripe-webhook-input"
                className="w-full px-4 py-3 bg-black border border-zinc-700 rounded-lg text-white placeholder-zinc-600 focus:border-yellow-500 outline-none font-mono text-sm"
              />
              <p className="text-xs text-zinc-500 mt-1 mb-4">
                Required only if you use Stripe webhooks for receipts.
              </p>

              <button
                onClick={save}
                disabled={saving || (!stripeKey && !webhookSecret)}
                data-testid="stripe-save-btn"
                className="w-full py-3 rounded-full font-bold bg-yellow-400 text-black hover:bg-yellow-300 disabled:bg-zinc-700 disabled:text-zinc-400 flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                Save & activate
              </button>

              {msg.text && (
                <div className={`mt-4 rounded-lg px-4 py-3 flex items-start gap-2 text-sm ${
                  msg.kind === 'ok' ? 'bg-green-500/10 border border-green-500/40 text-green-200' : 'bg-red-500/10 border border-red-500/40 text-red-200'
                }`} data-testid="stripe-msg">
                  {msg.kind === 'ok' ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                  <span>{msg.text}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatusRow = ({ label, value, pill }) => (
  <div className="flex items-center justify-between">
    <span className="text-zinc-500">{label}</span>
    <span className={pill ? `px-2 py-0.5 rounded-full text-xs font-bold ${pill}` : 'font-mono'}>
      {value}
    </span>
  </div>
);

export default AdminPaymentSettings;
